import type { DateRange } from "../model/aemet/aemet.ts";
import { daysBetweenIso } from "../model/isoDate.ts";
import { useT } from "../i18n/hooks.ts";

/**
 * Per-variable "which station covered which dates" detail — a raw per-day
 * tally ("station A: 10950") stops meaning anything once the fetched range
 * spans years, and never says WHEN a backup station kicked in or a real gap
 * happened. One collapsible per source (a station, or "no data"), sorted by
 * day count (busiest first) with that count always visible in the summary
 * and the concrete date ranges behind the toggle — shared by the
 * just-downloaded report (AemetFetchDialog.tsx, which groups its own live
 * per-day `sourceByDate` via `sourceRanges` before passing it down here) and
 * the persisted one (AemetProvenanceDialog.tsx, whose `AemetProvenance`
 * already stores these same grouped ranges directly — see storage.ts's own
 * comment on why: a per-day Map there was a real out-of-memory/multi-second
 * freeze at Duero scale, 1728 sub-basins) — either way this component only
 * ever renders already-grouped ranges, never regroups them itself.
 */
export function SourceCoverageDetail({
  bySource,
  stationName,
}: {
  bySource: Map<string | null, DateRange[]>;
  stationName: (code: string) => string;
}) {
  const t = useT();
  const entries = Array.from(bySource.entries())
    .map(([source, ranges]) => ({
      source,
      ranges,
      days: ranges.reduce((sum, r) => sum + daysBetweenIso(r.start, r.end) + 1, 0),
    }))
    .sort((a, b) => b.days - a.days);

  return (
    <>
      {entries.map(({ source, ranges, days }) => (
        <details key={source ?? "__sin_dato__"} className="collapsible-section">
          <summary>
            {t(days === 1 ? "aemetDialog.sourceOneDay" : "aemetDialog.sourceDays", {
              source: source === null ? t("aemetDialog.noData") : stationName(source),
              n: String(days),
            })}
          </summary>
          <ul className="zones-list">
            {ranges.map((r) => (
              <li key={`${r.start}_${r.end}`}>
                {t("aemetDialog.failedWindowRange", { from: r.start, to: r.end })}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </>
  );
}
